"use client";

import Booking from "@lib/classes/Booking";
import FrameConfig from "@lib/classes/FrameConfig";
import logoutProfile from "@services/logout_profile";
import reprice from "@services/reprice";
import Cookies from "js-cookie";

class Session extends Map {
  static addRoundtrip() {
    const firstLeg = this.getLeg(0);
    const newLeg: be.Reservation = { bagCount: firstLeg.bagCount };
    if (firstLeg.passengers) newLeg.passengers = [...firstLeg.passengers];
    if (firstLeg.pickup) newLeg.dropoff = { ...firstLeg.pickup };
    if (firstLeg.dropoff) newLeg.pickup = { ...firstLeg.dropoff };

    this.setLeg(1, newLeg);
  }

  // Returns true if any leg returns true for the function passed in
  static anyLeg(func: (leg: be.Reservation) => boolean): boolean {
    const booking = Session.getBooking();
    return booking.legs?.some(func);
  }

  // Clear all data entered by user so far (but keep things like attached user/group profiles)
  static clearBookingData(profilesIncluded = false) {
    const profiles = { ...this.getBooking().profiles };
    const bookingFlags = this.getBooking().flags;
    this.setBooking(new Booking());
    if (!profilesIncluded) this.updateBooking({ profiles });
    if (bookingFlags?.keepUserLoggedIn) {
      this.updateBooking({ profiles: { user: profiles.user } });
    }
  }

  // Clear data tied to a specific reservation and generally not reused for other reservations.
  static clearReservationData() {
    const booking = Session.getBooking();
    this.updateBooking({ legs: [], roundtrip: undefined });
    if (booking.otherInfo) booking.otherInfo.termsAndConditions = undefined;
  }

  // Reset the entire session, including attached profiles
  static async clearSession() {
    this.setBooking(new Booking());
    Session.remove("verifiedBooking");
    await logoutProfile("ALL");
    global.window?.apolloClient.clearStore(); // Clear GraphQL cache
    Cookies.remove(`b_${window.siteId}_gi`);
    new FrameConfig().apply();
  }

  // Move active booking to be a "confirmedBooking".
  // Reset the active booking.
  static confirmBooking() {
    const booking = Session.getBooking();
    Session.set("confirmedBooking", booking);
    Session.setBooking(new Booking());

    const profiles = booking.profiles;
    delete profiles.coupon;
    delete profiles.couponWeb;
    Session.updateBooking({ profiles: { ...profiles } });
    new FrameConfig().apply();
  }

  // Move active frp purchase to be a "confirmed".
  // Reset the active frp purchase.
  static confirmFrpPurchase() {
    const frpPurchase = Session.get("frpPurchase");
    Session.set("confirmedFrpPurchase", frpPurchase);
    Session.set("frpPurchase", undefined);
  }

  static editVerifiedBooking() {
    const verifiedBooking = Session.getVerifiedBooking();
    if (verifiedBooking) {
      // Make sure service area config matches booking.
      Session.updateConfig({ serviceArea: verifiedBooking.serviceArea });
      Session.setBooking(verifiedBooking);
    }
  }

  static setVerifiedBooking(booking: be.Booking) {
    Session.set("verifiedBooking", booking);
  }

  static defaultLegValues(leg: be.Reservation) {
    leg.passengers = [{ field: "passengers", value: "1" }];
    leg.serviceExtras = [];
    return leg;
  }

  static get(id: string, touchExpiration: boolean = true) {
    if (!global.sessionStorage) return null;
    if (touchExpiration && Session.isExpired()) {
      Session.touchExpiration();
      Session.clearSession();
    }
    if (touchExpiration) Session.touchExpiration();

    const value = sessionStorage.getItem(`${window.siteId}.${id}`);
    if (value === null) return null;
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  static remove(id: string) {
    if (!global.sessionStorage) return null;
    sessionStorage.removeItem(`${window.siteId}.${id}`);
  }

  static async removeRoundtrip(dontRecalculate = false) {
    const booking = this.getBooking();
    booking.roundtrip = false;
    booking.legs.splice(1, 1); // Remove second leg
    this.setBooking(booking);

    // Recalculate prices?
    if (!dontRecalculate) {
      const firstLeg = booking.legs[0];
      if (["recalculate", "regenerate"].includes(String(firstLeg.service?.roundtripPricingMode))) {
        await reprice(0, firstLeg.service?.roundtripPricingMode == "recalculate");
      }
    }
  }

  static set(id: string, value: string | object, touchExpiration: boolean = true) {
    if (!global.sessionStorage) return null;
    if (typeof value === "object") value = JSON.stringify(value);

    if (touchExpiration && Session.isExpired()) {
      Session.touchExpiration();
      Session.clearSession();
    }

    sessionStorage.setItem(`${window.siteId}.${id}`, value);
    if (touchExpiration) Session.touchExpiration();
  }

  static getBooking(): be.Booking {
    let booking = Session.get("booking");
    if (!booking) booking = new Booking();
    else booking = new Booking(booking);
    return booking;
  }

  static getConfig(): be.GlobalConfig {
    let config = Session.get("config");
    if (!config) config = {};
    return config;
  }

  static getConfirmedBooking(): be.Booking | undefined {
    let booking = Session.get("confirmedBooking");
    if (booking) booking = new Booking(booking);
    return booking;
  }

  static getConfirmedFrpPurchase(): be.FrequentRiderProgramSessionData | undefined {
    let purchase = Session.get("confirmedFrpPurchase");
    return purchase;
  }

  static getCurrentLeg(): be.Reservation | undefined {
    if (!global.window) return undefined;
    return Session.getLeg(window.legIndex);
  }

  static getCurrentLegIndex(): number {
    return window.legIndex;
  }

  static getVerifiedBooking(): be.Booking | undefined {
    let booking = Session.get("verifiedBooking");
    if (booking) booking = new Booking(booking);
    return booking;
  }

  static getLeg(legNum: number): be.Reservation {
    let leg = Session.getBooking().legs?.[legNum];
    if (!leg) leg = Session.defaultLegValues({});
    return leg;
  }

  static isExpired(minutesLeeway = 0): boolean {
    // Session expiration
    let expiration = Session.get("expiration", false);
    if (!expiration) return false;
    let expirationDate = new Date(expiration);
    let compareDate = new Date();
    compareDate.setMinutes(compareDate.getMinutes() + minutesLeeway);
    if (expirationDate.getTime() < compareDate.getTime()) return true;

    // JWT expiration
    expiration = Cookies.get(`b_${window.siteId}_e`);
    if (expiration) {
      expirationDate = new Date(expiration);
      compareDate = new Date();
      compareDate.setMinutes(compareDate.getMinutes() + minutesLeeway);
      if (expirationDate.getTime() < compareDate.getTime()) return true;
    }

    return false;
  }

  static isSecondLeg() {
    if (!global.sessionStorage) return null;
    return window.legIndex == 1;
  }

  static setBooking(booking: be.Booking): void {
    Session.set("booking", booking);
  }

  static setConfig(config: be.GlobalConfig): void {
    Session.set("config", config);
  }

  static setCurrentLeg(leg: be.Reservation): void {
    Session.setLeg(window.legIndex, leg);
  }

  static setLeg(legNum: number, leg: be.Reservation): void {
    const booking = Session.getBooking();
    booking.legs = booking.legs || [];
    booking.legs[legNum] = leg || {};
    Session.updateBooking({ legs: booking.legs });
  }

  // Session expiration is 2 days (rolling).
  static touchExpiration() {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 2);
    Session.set("expiration", expirationDate, false);
  }

  static updateBooking(values: Partial<be.Booking>): void {
    let oldBooking = Session.getBooking();
    let booking = { ...oldBooking, ...values };
    Session.setBooking(booking);
  }

  static updateConfig(values: Partial<be.GlobalConfig>): void {
    let oldConfig = Session.getConfig();
    let config = { ...oldConfig, ...values };
    Session.setConfig(config);
  }

  static updateCurrentLeg(values: Partial<be.Reservation>): void {
    Session.updateLeg(window.legIndex, values);
  }

  static updateFrpPurchase(values: Partial<be.FrequentRiderProgramSessionData>): void {
    const oldPurchase = Session.get("frpPurchase");
    const purchase = { ...oldPurchase, ...values };
    Session.set("frpPurchase", purchase);
  }

  static updateLeg(legNum: number, values: Partial<be.Reservation>): void {
    let leg = Session.getLeg(legNum);
    leg = { ...leg, ...values };
    Session.setLeg(legNum, leg);
  }
}

export default Session;
